// ==================== FOOTER COMPONENT ====================

import { Link } from 'react-router-dom';
import { Logo } from '../ui/Logo';
import { ROUTES, APP_CONSTANTS } from '../../constants';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';
import metaLogo from '../../assets/meta_logo.png';

const footerLinks = {
  product: {
    title: 'Product',
    links: [
      { label: 'Features', href: ROUTES.FEATURES },
      { label: 'Pricing', href: ROUTES.PRICING },
      { label: 'Integrations', href: '#' },
      { label: 'API Documentation', href: '#' },
    ],
  },
  company: {
    title: 'Company',
    links: [
      { label: 'About Us', href: ROUTES.ABOUT },
      { label: 'Contact', href: ROUTES.CONTACT },
      { label: 'Careers', href: '#' },
      { label: 'Blog', href: '#' },
    ],
  },
  legal: {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: ROUTES.PRIVACY },
      { label: 'Terms of Service', href: ROUTES.TERMS },
      { label: 'Cookie Policy', href: '#' },
      { label: 'GDPR', href: '#' },
    ],
  },
};

const socialLinks = [
  { icon: Instagram, href: 'https://www.instagram.com/amplifymsg/', label: 'Instagram' },
  { icon: Facebook, href: 'https://www.facebook.com/amplifymsg/', label: 'Facebook' },
  { icon: Youtube, href: 'https://www.youtube.com/@amplifymsg', label: 'YouTube' },
];
  
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-(--color-dark-navy)">
      {/* Main Footer */}
      <div className="container-custom py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Logo size="lg" className="mb-4" />
            <p className="text-(--color-muted-gray) text-sm leading-relaxed mb-6 max-w-md">
              {APP_CONSTANTS.APP_DESCRIPTION}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href={`mailto:${APP_CONSTANTS.CONTACT_EMAIL}`}
                className="flex items-center gap-3 text-sm text-(--color-soft-gray) hover:text-(--color-signal-orange) transition-colors"
              >
                <Mail size={16} />
                {APP_CONSTANTS.CONTACT_EMAIL}
              </a>
              <a
                href={`tel:${APP_CONSTANTS.CONTACT_PHONE}`}
                className="flex items-center gap-3 text-sm text-(--color-soft-gray) hover:text-(--color-signal-orange) transition-colors"
              >
                <Phone size={16} />
                {APP_CONSTANTS.CONTACT_PHONE}
              </a>
              <div className="flex items-start gap-3 text-sm text-(--color-soft-gray)">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                18 Zaman Park, Lane 2, Canal Road, Lahore, Pakistan
              </div>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{footerLinks.product.title}</h4>
            <ul className="space-y-3">
              {footerLinks.product.links.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-(--color-muted-gray) hover:text-(--color-signal-orange) transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{footerLinks.company.title}</h4>
            <ul className="space-y-3">
              {footerLinks.company.links.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-(--color-muted-gray) hover:text-(--color-signal-orange) transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{footerLinks.legal.title}</h4>
            <ul className="space-y-3">
              {footerLinks.legal.links.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-(--color-muted-gray) hover:text-(--color-signal-orange) transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Meta Partner Badges */}
      <div className="border-t border-white/5">
        <div className="container-custom py-6">
          <div className="flex flex-wrap items-center justify-center gap-6">
            {/* Meta Verified Badge */}
            <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full px-5 py-2.5">
              <img src={metaLogo} alt="Meta" className="w-6 h-5 object-contain" />
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-white">Meta Verified</span>
                <svg viewBox="0 0 20 20" className="w-4 h-4 text-[#0866FF]" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Meta Business Partner Badge */}
            <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full px-5 py-2.5">
              <img src={metaLogo} alt="Meta" className="w-6 h-5 object-contain" />
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-white">Meta Business Partner</span>
                <svg viewBox="0 0 20 20" className="w-4 h-4 text-[#0866FF]" fill="currentColor">
                  <path fillRule="evenodd" d="M16.403 12.652a3 3 0 0 0 0-5.304 3 3 0 0 0-3.75-3.751 3 3 0 0 0-5.305 0 3 3 0 0 0-3.751 3.75 3 3 0 0 0 0 5.305 3 3 0 0 0 3.75 3.751 3 3 0 0 0 5.305 0 3 3 0 0 0 3.751-3.75Zm-2.546-4.46a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* WhatsApp Business API Badge */}
            <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full px-5 py-2.5">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#25D366]" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
              </svg>
              <span className="text-sm font-semibold text-white">WhatsApp Business API</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-(--color-muted-gray)">
              © {currentYear} {APP_CONSTANTS.APP_NAME}. All rights reserved.
              <span className="mx-2">•</span>
              <span>Powered by <a href="https://nkutechnologies.com" target="_blank" rel="noopener noreferrer" className="text-(--color-signal-orange) hover:underline">NKU Technologies (PVT) Ltd.</a></span>
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="text-(--color-muted-gray) hover:text-(--color-signal-orange) transition-colors"
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Footer };

